import styled, { css } from 'styled-components';

import { Text } from '@/nova/components';
import { hoverWash } from '@/nova/theme/mixins';
import { CUST, CUST_ANIM, CUST_GRADIENTS } from '../../../../theme/customizer-tokens';

/* ── Desktop ─────────────────────────────────────────────────────────────── */

export const DesktopBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

/** Solid dark card holding the input / the DNS records. */
export const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 24px;
  background: ${CUST.surfaceCard};
  overflow: hidden;
`;

/** Padded section inside a card (24px horizontal to match Figma). */
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px;
`;

export const Label = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: ${CUST.textMuted};
`;

export const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${CUST.panelDivider};
`;

/** Full-width "Link your domain" CTA inside the input card (grey while disabled /
 *  fetching); it calls the DNS-config API (`submitConnectDomain`). */
export const LinkBtn = styled.button<{ $enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 100%;
  padding: 0 24px;
  border: none;
  border-radius: 9999px;
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'not-allowed')};
  background: ${({ $enabled }) => ($enabled ? CUST.accent : CUST.connectBtnDisabledBg)};
  transition: filter ${CUST_ANIM.INTERACT_MS}ms ease;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  &:hover {
    filter: ${({ $enabled }) => ($enabled ? 'brightness(1.06)' : 'none')};
  }

  ${(p) =>
    !p.$enabled &&
    css`
      &:hover::before,
      &:hover::after,
      &:focus-visible::before,
      &:focus-visible::after {
        opacity: 0;
      }
    `}
`;

export const LinkBtnLabel = styled(Text)<{ $enabled: boolean }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
  color: ${({ $enabled }) => ($enabled ? CUST.onAccent : CUST.connectBtnDisabledText)};
`;

/** Inline error under the domain field when the typed domain can't be linked
 *  (e.g. it's already used by another instance — the API's ERR_1003). */
export const ErrorNote = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  color: ${CUST.connectErrorText};
`;

/* DNS records screen ------------------------------------------------------- */

/** Read-only "chosen domain to link" field with a pencil edit affordance. */
export const ChosenField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 72px;
  padding: 12px 24px;
  border-radius: 22px;
  background: ${CUST.chosenFieldBg};
`;

export const ChosenValue = styled(Text)`
  min-width: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: ${CUST.white};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EditBtn = styled.button`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${CUST.textMuted};
  cursor: pointer;
  transition: color ${CUST_ANIM.INTERACT_MS}ms ease;

  &:hover {
    color: ${CUST.white};
  }
`;

export const Heading = styled(Text)`
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
  color: ${CUST.dnsHeading};
`;

export const Body = styled(Text)`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.45;
  color: ${CUST.dnsBody};
`;

export const ToggleBtn = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const ToggleLabel = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;
  color: ${CUST.dnsToggle};
`;

/** Collapsible wrapper for the DNS records (accordion). Animates height via the
 *  grid-template-rows 0fr -> 1fr trick, so it works without a fixed height. */
export const Accordion = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows ${CUST_ANIM.SCREEN_MS}ms ${CUST_ANIM.SCREEN_EASE};
`;

export const AccordionInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

/** Sticky footer on the DNS card — the "Connect with {Entri}" auto-setup CTA. */
export const ConnectFooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  background-image: ${CUST_GRADIENTS.connectFooter};
`;

export const ProviderBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  border: none;
  border-radius: 9999px;
  background: ${CUST.accent};
  cursor: pointer;
  transition: filter ${CUST_ANIM.INTERACT_MS}ms ease;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  &:hover {
    filter: brightness(1.06);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
    filter: none;
  }

  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;

export const ProviderBtnLabel = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
  color: ${CUST.onAccent};
`;

/** Entri brand wordmark (CDN SVG) shown in the "Connect with …" CTA — replaces
 *  the registrar name; the black wordmark reads on the orange button. */
export const ENTRI_LOGO = 'https://micros-t.b-cdn.net/entri-logo-1779877516162.svg';

export const EntriLogo = styled.img`
  display: block;
  height: 18px;
  width: auto;
`;

/* ── Mobile ──────────────────────────────────────────────────────────────── */

export const MobileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: 100%;
`;

export const MobileHeading = styled(Text)`
  margin-top: 4px;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.1;
  text-align: center;
  background-image: ${CUST_GRADIENTS.headingGradient};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const MobileSubtext = styled(Text)`
  max-width: 320px;
  font-size: 13px;
  line-height: 1.4;
  text-align: center;
  color: ${CUST.textMuted};
`;

/** Mobile inline error under the domain field (e.g. the domain is already used). */
export const MobileErrorNote = styled(Text)`
  max-width: 320px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  color: ${CUST.connectErrorText};
`;

export const MobileSettingsNote = styled(Text)`
  max-width: 320px;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
  color: ${CUST.textFaint};
`;

export const MobileSectionLabel = styled(Text)`
  align-self: flex-start;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${CUST.textMuted};
`;

/** Tappable "Required DNS records" header that expands the record cards (mobile
 *  accordion). The label inside reuses `MobileSectionLabel`. */
export const MobileDnsToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

export const MobileDnsChevron = styled.span<{ $open: boolean }>`
  flex: 0 0 auto;
  display: inline-flex;
  color: ${CUST.textMuted};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform ${CUST_ANIM.INTERACT_MS}ms ease;
`;

export const MobileField = styled.div`
  width: 100%;
`;

/** Full-width "Connect with {Entri}" auto-setup button on the mobile DNS screen. */
export const MobileEntriBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  margin-top: 8px;
  border: none;
  border-radius: 9999px;
  background: ${CUST.accent};
  cursor: pointer;
  transition: filter ${CUST_ANIM.INTERACT_MS}ms ease;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}

  &:disabled {
    cursor: default;
    opacity: 0.6;
    filter: none;
  }

  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;